import { useRef, useState } from 'react';

const POSITION_MAX = 9999;

type Props = {
  /** Operator sort order; null keeps the category in the default order. */
  position: number | null;
  onSave: (position: number) => Promise<void>;
  onReset: () => Promise<void>;
};

// '' clears the order (null); undefined marks input that must not be sent.
function parsePosition(raw: string): number | null | undefined {
  const value = raw.trim();
  if (value === '') return null;
  if (!/^\d+$/.test(value)) return undefined;
  const position = Number(value);
  return position <= POSITION_MAX ? position : undefined;
}

export function OrderField({ position, onSave, onReset }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // A backend without the order field omits it: read that as "default order".
  const current = position ?? null;
  const isOverride = current !== null;

  const startEditing = () => {
    setDraft(isOverride ? String(current) : '');
    setIsEditing(true);
  };

  const commit = async () => {
    // A number input reports unparseable text ("e", "-") as an empty value;
    // badInput keeps that from passing for a deliberately cleared field.
    const next = inputRef.current?.validity.badInput ? undefined : parsePosition(draft);
    setIsEditing(false);
    if (next === undefined || next === current) return;
    setIsSaving(true);
    try {
      await (next === null ? onReset() : onSave(next));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    try {
      await onReset();
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void commit();
    if (e.key === 'Escape') setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[12px] text-[var(--co-muted)]">Order</span>
        <input
          ref={inputRef}
          autoFocus
          type="number"
          inputMode="numeric"
          min={0}
          max={POSITION_MAX}
          step={1}
          value={draft}
          placeholder="Default"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => void commit()}
          aria-label="Category order"
          className="w-20 rounded border border-[var(--co-brand)] bg-[var(--co-bg)] px-1.5 py-0.5 text-[13px] text-[var(--co-text)] outline-none"
        />
        <button
          type="button"
          // preventDefault keeps the input from blurring first, so the save
          // runs once — via onClick, not via the input's onBlur.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => void commit()}
          className="shrink-0 rounded bg-[var(--co-brand)] px-2 py-0.5 text-[11px] font-semibold text-white"
        >
          OK
        </button>
      </div>
    );
  }

  return (
    <div className="group/order flex items-center gap-1">
      <span className="text-[12px] text-[var(--co-muted)]">Order</span>
      {isOverride ? (
        <span className="text-[12px] font-medium text-[var(--co-text)]">{current}</span>
      ) : (
        <span className="text-[12px] text-[var(--co-muted)]" title="Default order">
          —
        </span>
      )}
      <button
        type="button"
        onClick={startEditing}
        disabled={isSaving}
        title="Edit category order"
        aria-label="Edit category order"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--co-muted)] opacity-0 transition-opacity hover:bg-[var(--co-bg)] hover:text-[var(--co-text)] group-hover/order:opacity-100"
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M11.1 2.2a1.4 1.4 0 0 1 2 2L5.4 11.9l-2.7.7.7-2.7 7.7-7.7Z" strokeLinejoin="round" />
        </svg>
      </button>
      {isOverride && (
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={isSaving}
          title="Reset to default order"
          aria-label="Reset category order to default"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--co-muted)] opacity-0 transition-opacity hover:bg-[var(--co-bg)] hover:text-[var(--co-text)] group-hover/order:opacity-100"
        >
          <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 8a5 5 0 1 1 1.5 3.6M3 8V4.5M3 8h3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
